import { useState } from "react";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { Button } from "../../components/button.js";
import type { WizardState, ScanFrequency } from "../../api/types.js";

interface FrequencyOption {
  value: ScanFrequency;
  label: string;
  description: string;
  recommended?: boolean;
}

const OPTIONS: FrequencyOption[] = [
  {
    value: "monthly",
    label: "Monthly",
    description:
      "Re-scans automatically once a month. Best for active sites that change regularly.",
    recommended: true,
  },
  {
    value: "quarterly",
    label: "Quarterly",
    description:
      "Re-scans every three months. Good for stable sites with infrequent updates.",
  },
  {
    value: "manual",
    label: "Manual only",
    description:
      "No automatic rescanning. You trigger scans yourself when ready.",
  },
];

/** Max scans per month for each plan (sourced from plan-seed.ts) */
const PLAN_SCANS_PER_MONTH: Record<string, number> = {
  free: 1,
  starter: 2,
  growth: 4,
  pro: 8,
  enterprise: 9999,
};

interface ScanFrequencyStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function ScanFrequencyStep({
  websiteId,
  wizardState,
}: ScanFrequencyStepProps) {
  const existingData = wizardState.steps.find(
    (s) => s.step === "scan_frequency",
  )?.data;

  // Get plan's monthly scan allowance for info text
  const planData = wizardState.steps.find(
    (s) => s.step === "plan_selection",
  )?.data;
  const planId = (planData?.planId as string | undefined) ?? "free";
  const scansPerMonth = PLAN_SCANS_PER_MONTH[planId] ?? 1;

  const existingFreq =
    (existingData?.frequency as ScanFrequency | undefined) ?? "monthly";

  const [selected, setSelected] = useState<ScanFrequency>(existingFreq);
  const advance = useAdvanceWizardStep(websiteId);

  function handleContinue() {
    advance.mutate({
      step: "scan_frequency",
      outcome: "pass",
      data: { frequency: selected },
    });
  }

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Scan frequency</h2>
      <p className="wizard-content__subtitle">
        How often should AccessiMonitor re-scan your website automatically?
      </p>

      <div className="option-grid" role="radiogroup" aria-label="Scan frequency options">
        {OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className={`option-card${selected === opt.value ? " option-card--selected" : ""}${opt.recommended ? " option-card--recommended" : ""}`}
            onClick={() => setSelected(opt.value)}
            role="radio"
            aria-checked={selected === opt.value}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelected(opt.value);
            }}
          >
            <p className="option-card__title">{opt.label}</p>
            <p className="option-card__desc">{opt.description}</p>
          </div>
        ))}
      </div>

      <p className="text-sm text-muted">
        Your plan includes {scansPerMonth} {scansPerMonth === 1 ? "scan" : "scans"} per
        month. You can also trigger scans manually at any time within this limit.
      </p>

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      <div className="wizard-content__actions">
        <Button onClick={handleContinue} loading={advance.isPending}>
          Continue
        </Button>
      </div>
    </div>
  );
}
